import React, { useState, useEffect, useCallback } from "react";

import {
  View,
  Text,
  Image,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Pressable,
  ActivityIndicator,
  Modal,
  TextInput,
  // eslint-disable-next-line react-native/split-platform-components
  ToastAndroid,
} from "react-native";

import t from "@constants/localization";
import colors from "@constants/color";
import BackArrow from "@assets/svg/BackArrow";
import imagePickerHelper from "@utils/imagePickerHelper";
import getCurrentLocation from "@utils/getCurrentLocation";
import useHandoverVideos from "@hooks/useHandoverVideos";
import {
  initHandoverVideo,
  completeHandoverVideo,
  discardHandoverVideo,
  setHandoverFoundQty,
  bypassHandoverVideo,
  uploadToPresignedUrl,
  localFileExists,
  reportHandoverVideoFailure,
  getHandoverCrateItems,
} from "@services/api";
import {
  savedVideoKey,
  getSavedVideos,
  saveVideo,
  removeSavedVideo,
} from "@utils/handoverVideoStore";

const PATH = "screens/CrateHandoverVideos";

const FLOW_TYPES = {
  CRATE_HANDOVER: "CRATE_HANDOVER",
  RVP_CRATE_PICKUP: "RVP_CRATE_PICKUP",
};

// Partner classifies an RVP crate before recording. condition x category
// enum values must match commons/videoCaptures CRATE_CONDITIONS/CRATE_CATEGORIES.
const RVP_CLASSIFICATION_OPTIONS = [
  { condition: "BAD", category: "OIL", labelKey: "badOil" },
  { condition: "BAD", category: "NON_OIL", labelKey: "badNonOil" },
  { condition: "GOOD", category: "OIL", labelKey: "goodOil" },
  { condition: "GOOD", category: "NON_OIL", labelKey: "goodNonOil" },
];

const showToast = (msg) =>
  ToastAndroid.showWithGravity(msg, ToastAndroid.SHORT, ToastAndroid.CENTER);

const fmtDuration = (s) => {
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return null;
  const total = Math.round(n);
  const mm = Math.floor(total / 60);
  const ss = String(total % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

const fmtSize = (bytes) => {
  const n = Number(bytes);
  return Number.isFinite(n) && n > 0 ? `${(n / 1048576).toFixed(1)} MB` : null;
};

const StatusChip = ({ status }) => {
  const styleByStatus = {
    UPLOADED: {
      backgroundColor: colors.plain_green,
      label: t(`${PATH}/uploaded`),
    },
    BYPASSED: { backgroundColor: colors.orange, label: t(`${PATH}/bypassed`) },
    INITIATED: {
      backgroundColor: colors.orange,
      label: t(`${PATH}/uploadPending`),
    },
    FAILED: { backgroundColor: colors.plain_red, label: t(`${PATH}/failed`) },
    PENDING: {
      backgroundColor: colors.lightBlack,
      label: t(`${PATH}/pending`),
    },
  };
  const { backgroundColor, label } =
    styleByStatus[status] || styleByStatus.PENDING;
  return (
    <View style={[styles.chip, { backgroundColor }]}>
      <Text style={styles.chipText}>{label}</Text>
    </View>
  );
};

const CrateHandoverVideos = ({ navigation, route }) => {
  const { date } = route?.params || {};
  const {
    loading,
    enabled,
    bypass_allowed,
    reached,
    crates,
    counts,
    refetch,
    // Per-flow found-item-qty config: { CRATE_HANDOVER: {enabled, mandatory},
    // RVP_CRATE_PICKUP: {...} }. Absent on older backends → feature stays off.
    found_item_qty: foundQtyConfig,
  } = useHandoverVideos(date);
  // crate_id currently recording/uploading — locks that row's buttons
  const [busyCrateId, setBusyCrateId] = useState(null);
  const [bypassTarget, setBypassTarget] = useState(null); // crate row or null
  const [bypassReason, setBypassReason] = useState("");
  // RVP crate awaiting condition/category selection before recording
  const [classifyTarget, setClassifyTarget] = useState(null);
  // View-details modal: the crate whose contents are shown, its items + loading
  const [detailsCrate, setDetailsCrate] = useState(null);
  const [detailsItems, setDetailsItems] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  // Recordings whose upload failed, kept on the phone for retry-without-
  // re-recording. Keyed by savedVideoKey(crate).
  const [savedVideos, setSavedVideos] = useState({});
  // Upload progress percent for the busy crate (null → indeterminate spinner)
  const [uploadPct, setUploadPct] = useState(null);
  // Unsaved found-qty text per crate_id (before the partner taps Save). Falls
  // back to the crate's persisted found_item_qty when absent.
  const [qtyDraft, setQtyDraft] = useState({});
  // crate_id whose found-qty is being saved — locks its input/button.
  const [qtySavingCrateId, setQtySavingCrateId] = useState(null);
  // Set only while bytes are actually moving to S3 (not while the camera is
  // open) — drives the "keep the app open" banner.
  const [uploadingCrateId, setUploadingCrateId] = useState(null);

  const onUploadProgress = (sent, total) =>
    setUploadPct(
      total > 0 ? Math.min(Math.floor((sent / total) * 100), 99) : null,
    );

  const refreshSaved = useCallback(() => {
    getSavedVideos()
      .then(setSavedVideos)
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshSaved();
  }, [refreshSaved]);

  // A crate can get UPLOADED/BYPASSED through another path (re-record,
  // bypass) — drop its saved recording so the retry button disappears.
  useEffect(() => {
    crates.forEach((c) => {
      const key = savedVideoKey(c);
      if (
        (c.video_status === "UPLOADED" || c.video_status === "BYPASSED") &&
        savedVideos[key]
      ) {
        removeSavedVideo(key).then(refreshSaved);
      }
    });
  }, [crates, savedVideos, refreshSaved]);

  const openDetails = async (crate) => {
    setDetailsCrate(crate);
    setDetailsItems([]);
    setDetailsLoading(true);
    try {
      const { data } = await getHandoverCrateItems(
        crate.crate_id,
        crate.flow_type,
      );
      setDetailsItems(data?.items || []);
    } catch {
      // interceptor toasts server error
    } finally {
      setDetailsLoading(false);
    }
  };

  const recordVideo = async (crate, classification) => {
    setBusyCrateId(crate.crate_id);
    try {
      // Location is audit-only; never block recording on a GPS failure.
      const location = await getCurrentLocation().catch(() => null);
      const { data: initData } = await initHandoverVideo({
        crate_id: crate.crate_id,
        type: crate.flow_type,
        lat: location?.coords?.latitude,
        lng: location?.coords?.longitude,
        crate_condition: classification?.condition,
        crate_category: classification?.category,
      });

      imagePickerHelper({
        source: "camera",
        mediaType: "video",
        cameraType: "back",
        durationLimit: initData?.limits?.max_duration_s || 90,
        skipUpload: true,
        onSuccessCallback: async (response) => {
          try {
            setUploadingCrateId(crate.crate_id);
            await uploadToPresignedUrl(
              initData.upload_url,
              response.uri,
              initData.content_type,
              onUploadProgress,
            );
            await completeHandoverVideo(initData.id, response.duration);
            await removeSavedVideo(savedVideoKey(crate)).catch(() => {});
            showToast(t(`${PATH}/uploadSuccess`));
          } catch (err) {
            // Keep the recording on the phone so the partner can retry the
            // upload without recording again, and tell the backend why the
            // upload died (fire-and-forget) so the row goes FAILED instead of
            // sitting INITIATED.
            reportHandoverVideoFailure(
              initData.id,
              String(err?.message || "upload failed"),
            ).catch(() => {});
            await saveVideo(savedVideoKey(crate), {
              uri: response.uri,
              duration: response.duration,
              classification: classification || null,
            }).catch(() => {});
            showToast(t(`${PATH}/uploadFailed`));
          } finally {
            setUploadingCrateId(null);
            setUploadPct(null);
            refreshSaved();
            setBusyCrateId(null);
            refetch();
          }
        },
        onSelectCallback: (response) => {
          if (!response || response.didCancel || !response.uri) {
            setBusyCrateId(null);
            refetch();
          }
        },
      });
    } catch {
      // axios interceptor already toasts the server error message
      setBusyCrateId(null);
      refetch();
    }
  };

  // Re-upload the recording already saved on the phone — no camera. A fresh
  // init gets a new presigned URL (the old one may have expired) and reuses
  // the RVP classification chosen at record time.
  const retryUpload = async (crate) => {
    const key = savedVideoKey(crate);
    const entry = savedVideos[key];
    if (!entry) return;
    setBusyCrateId(crate.crate_id);
    let captureId = null;
    try {
      if (!(await localFileExists(entry.uri))) {
        // Android purged the cached recording — nothing left to retry.
        await removeSavedVideo(key).catch(() => {});
        refreshSaved();
        showToast(t(`${PATH}/savedVideoGone`));
        return;
      }
      const location = await getCurrentLocation().catch(() => null);
      const { data: initData } = await initHandoverVideo({
        crate_id: crate.crate_id,
        type: crate.flow_type,
        lat: location?.coords?.latitude,
        lng: location?.coords?.longitude,
        crate_condition: entry.classification?.condition,
        crate_category: entry.classification?.category,
      });
      captureId = initData.id;
      setUploadingCrateId(crate.crate_id);
      await uploadToPresignedUrl(
        initData.upload_url,
        entry.uri,
        initData.content_type,
        onUploadProgress,
      );
      await completeHandoverVideo(initData.id, entry.duration);
      await removeSavedVideo(key).catch(() => {});
      refreshSaved();
      showToast(t(`${PATH}/uploadSuccess`));
    } catch (err) {
      // Upload failed again — keep the saved file for another retry, and
      // record why on the backend row (only once init has produced one).
      if (captureId) {
        reportHandoverVideoFailure(
          captureId,
          String(err?.message || "upload failed"),
        ).catch(() => {});
      }
      showToast(t(`${PATH}/uploadFailed`));
    } finally {
      setUploadingCrateId(null);
      setUploadPct(null);
      setBusyCrateId(null);
      refetch();
    }
  };

  const submitBypass = async () => {
    if (!bypassReason.trim()) {
      showToast(t(`${PATH}/reasonRequired`));
      return;
    }
    const target = bypassTarget;
    setBypassTarget(null);
    setBusyCrateId(target.crate_id);
    try {
      await bypassHandoverVideo({
        crate_ids: [target.crate_id],
        reason: bypassReason.trim(),
      });
      showToast(t(`${PATH}/bypassDone`));
    } catch {
      // interceptor toasts server error
    } finally {
      setBypassReason("");
      setBusyCrateId(null);
      refetch();
    }
  };

  const discardVideo = async (item) => {
    setBusyCrateId(item.crate_id);
    try {
      await discardHandoverVideo(item.video_capture_id);
      showToast(t(`${PATH}/discarded`));
    } catch {
      // interceptor toasts server error
    } finally {
      setBusyCrateId(null);
      refetch();
    }
  };

  // Persist the found-item qty for a crate (independent of the video). The
  // partner can do this before or after recording.
  const submitFoundQty = async (crate) => {
    const draft = qtyDraft[crate.crate_id];
    const raw =
      draft !== undefined
        ? draft
        : crate.found_item_qty != null
          ? String(crate.found_item_qty)
          : "";
    const n = Number(raw);
    if (raw === "" || !Number.isInteger(n) || n < 0) {
      showToast(t(`${PATH}/qtyInvalid`));
      return;
    }
    setQtySavingCrateId(crate.crate_id);
    try {
      await setHandoverFoundQty(crate.crate_id, crate.flow_type, n);
      setQtyDraft((d) => {
        const next = { ...d };
        delete next[crate.crate_id];
        return next;
      });
      showToast(t(`${PATH}/qtySaved`));
    } catch {
      // axios interceptor toasts the server error
    } finally {
      setQtySavingCrateId(null);
      refetch();
    }
  };

  const renderCrate = ({ item }) => {
    const isBusy = busyCrateId === item.crate_id;
    const isDone =
      item.video_status === "UPLOADED" || item.video_status === "BYPASSED";
    const canBypass = bypass_allowed && !isDone && reached;
    // Discard (✕) is offered only for a successfully uploaded video.
    const hasCapture =
      !!item.video_capture_id && item.video_status === "UPLOADED";
    const durationText = fmtDuration(item.video_duration_s);
    const sizeText = fmtSize(item.video_size_bytes);
    // A recording saved on the phone after a failed upload — offer retrying
    // the upload without opening the camera again.
    const savedEntry = !isDone ? savedVideos[savedVideoKey(item)] : null;
    const recordLabel = savedEntry
      ? t(`${PATH}/reRecord`)
      : item.video_status === "UPLOADED"
        ? t(`${PATH}/reRecord`)
        : item.video_status === "INITIATED" || item.video_status === "FAILED"
          ? t(`${PATH}/retry`)
          : t(`${PATH}/record`);

    // Found-item qty: shown when enabled for this crate's flow. Value is the
    // unsaved draft if any, else the persisted qty. Save enables only when the
    // entered value is non-empty and differs from what's stored.
    const qtyCfg = foundQtyConfig?.[item.flow_type];
    const persistedQty =
      item.found_item_qty != null ? String(item.found_item_qty) : "";
    const qtyDraftVal = qtyDraft[item.crate_id];
    const qtyValue = qtyDraftVal !== undefined ? qtyDraftVal : persistedQty;
    const qtyDirty = qtyValue !== "" && qtyValue !== persistedQty;
    const qtySaving = qtySavingCrateId === item.crate_id;
    const qtyMissing = qtyCfg?.mandatory && item.found_item_qty == null;

    return (
      <View style={styles.card}>
        <View style={styles.cardTop}>
          <View style={styles.cardInfo}>
            <Text style={styles.crateId}>{item.crate_id}</Text>
            <Text style={styles.numItems}>
              {t(`${PATH}/numItems`, { count: String(item.num_items) })}
            </Text>
          </View>
          <StatusChip status={item.video_status} />
        </View>
        {qtyCfg?.enabled ? (
          <View style={styles.qtyRow}>
            <Text style={styles.qtyLabel}>
              {t(`${PATH}/foundQtyLabel`)}
              {qtyCfg.mandatory ? (
                <Text style={styles.qtyRequired}> *</Text>
              ) : null}
            </Text>
            <TextInput
              style={[styles.qtyInput, qtyMissing && styles.qtyInputMissing]}
              keyboardType="number-pad"
              placeholder={t(`${PATH}/foundQtyPlaceholder`)}
              placeholderTextColor={colors.lightBlack}
              editable={!qtySaving}
              value={qtyValue}
              maxLength={5}
              onChangeText={(txt) =>
                setQtyDraft((d) => ({
                  ...d,
                  [item.crate_id]: txt.replace(/[^0-9]/g, ""),
                }))
              }
            />
            <TouchableOpacity
              style={[
                styles.qtySaveBtn,
                (!qtyDirty || qtySaving) && styles.qtySaveBtnDisabled,
              ]}
              disabled={!qtyDirty || qtySaving}
              onPress={() => submitFoundQty(item)}
            >
              {qtySaving ? (
                <ActivityIndicator size="small" color={colors.white} />
              ) : (
                <Text style={styles.qtySaveBtnText}>
                  {t(`${PATH}/foundQtySave`)}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        ) : null}
        {reached && hasCapture ? (
          <View style={styles.videoInfoRow}>
            <Text style={styles.videoInfoText}>
              {[durationText, sizeText].filter(Boolean).join(" · ") ||
                t(`${PATH}/videoReady`)}
            </Text>
            <TouchableOpacity
              style={styles.discardBtn}
              disabled={isBusy}
              onPress={() => discardVideo(item)}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Text style={styles.discardIcon}>✕</Text>
            </TouchableOpacity>
          </View>
        ) : null}
        {reached && savedEntry ? (
          <View style={styles.videoInfoRow}>
            <Text style={styles.videoInfoText}>
              {[t(`${PATH}/savedVideoInfo`), fmtDuration(savedEntry.duration)]
                .filter(Boolean)
                .join(" · ")}
            </Text>
          </View>
        ) : null}
        {reached ? (
          <View style={styles.cardActions}>
            {savedEntry ? (
              <TouchableOpacity
                style={[styles.actionBtn, styles.recordBtn]}
                disabled={isBusy}
                onPress={() => retryUpload(item)}
              >
                {isBusy ? (
                  uploadPct != null ? (
                    <Text style={styles.recordBtnText}>{uploadPct}%</Text>
                  ) : (
                    <ActivityIndicator size="small" color={colors.white} />
                  )
                ) : (
                  <Text style={styles.recordBtnText}>
                    {t(`${PATH}/retryUpload`)}
                  </Text>
                )}
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              style={[
                styles.actionBtn,
                savedEntry ? styles.skipBtn : styles.recordBtn,
              ]}
              disabled={isBusy}
              onPress={() =>
                item.flow_type === FLOW_TYPES.RVP_CRATE_PICKUP
                  ? setClassifyTarget(item)
                  : recordVideo(item)
              }
            >
              {isBusy && !savedEntry ? (
                uploadPct != null ? (
                  <Text style={styles.recordBtnText}>{uploadPct}%</Text>
                ) : (
                  <ActivityIndicator size="small" color={colors.white} />
                )
              ) : (
                <Text
                  style={savedEntry ? styles.skipBtnText : styles.recordBtnText}
                >
                  {recordLabel}
                </Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.actionBtn, styles.skipBtn]}
              onPress={() => openDetails(item)}
            >
              <Text style={styles.skipBtnText}>{t(`${PATH}/viewDetails`)}</Text>
            </TouchableOpacity>
            {canBypass ? (
              <TouchableOpacity
                style={[styles.actionBtn, styles.skipBtn]}
                disabled={isBusy}
                onPress={() => setBypassTarget(item)}
              >
                <Text style={styles.skipBtnText}>{t(`${PATH}/skip`)}</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ) : null}
      </View>
    );
  };

  const deliveryCrates = crates.filter(
    (c) => c.flow_type === FLOW_TYPES.CRATE_HANDOVER,
  );
  const rvpCrates = crates.filter(
    (c) => c.flow_type === FLOW_TYPES.RVP_CRATE_PICKUP,
  );
  const sections = [
    ...(deliveryCrates.length
      ? [{ header: t(`${PATH}/deliverySection`) }, ...deliveryCrates]
      : []),
    ...(rvpCrates.length
      ? [{ header: t(`${PATH}/rvpSection`) }, ...rvpCrates]
      : []),
  ];

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <BackArrow />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t(`${PATH}/title`)}</Text>
        {counts.total ? (
          <Text style={styles.headerCount}>
            {counts.done}/{counts.total}
          </Text>
        ) : null}
      </View>

      {loading ? (
        <ActivityIndicator style={styles.loader} color={colors.primary} />
      ) : !enabled ? (
        <Text style={styles.emptyText}>{t(`${PATH}/notEnabled`)}</Text>
      ) : (
        <>
          {!reached ? (
            <View style={styles.lockedBanner}>
              <Text style={styles.lockedText}>{t(`${PATH}/lockedBanner`)}</Text>
            </View>
          ) : null}
          {uploadingCrateId ? (
            <View style={styles.uploadingBanner}>
              <Text style={styles.lockedText}>
                {t(`${PATH}/uploadingBanner`)}
                {uploadPct != null ? ` (${uploadPct}%)` : ""}
              </Text>
            </View>
          ) : null}
          <FlatList
            data={sections}
            keyExtractor={(item, index) =>
              item.header ? `h-${index}` : `${item.flow_type}-${item.crate_id}`
            }
            renderItem={({ item }) =>
              item.header ? (
                <Text style={styles.sectionHeader}>{item.header}</Text>
              ) : (
                renderCrate({ item })
              )
            }
            refreshing={loading}
            onRefresh={refetch}
            ListEmptyComponent={
              <Text style={styles.emptyText}>{t(`${PATH}/noCrates`)}</Text>
            }
            contentContainerStyle={styles.listContent}
          />
        </>
      )}

      <Modal
        visible={!!bypassTarget}
        transparent
        animationType="fade"
        onRequestClose={() => setBypassTarget(null)}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>
              {t(`${PATH}/bypassTitle`, {
                crate: bypassTarget?.crate_id || "",
              })}
            </Text>
            <Text style={styles.modalWarning}>
              {t(`${PATH}/bypassWarning`)}
            </Text>
            <TextInput
              style={styles.reasonInput}
              placeholder={t(`${PATH}/reasonPlaceholder`)}
              value={bypassReason}
              onChangeText={setBypassReason}
              multiline
            />
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.actionBtn, styles.skipBtn]}
                onPress={() => {
                  setBypassTarget(null);
                  setBypassReason("");
                }}
              >
                <Text style={styles.skipBtnText}>{t(`${PATH}/cancel`)}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionBtn, styles.recordBtn]}
                onPress={submitBypass}
              >
                <Text style={styles.recordBtnText}>
                  {t(`${PATH}/confirmBypass`)}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        visible={!!classifyTarget}
        transparent
        animationType="fade"
        onRequestClose={() => setClassifyTarget(null)}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>
              {t(`${PATH}/classifyTitle`, {
                crate: classifyTarget?.crate_id || "",
              })}
            </Text>
            <Text style={styles.classifyHint}>{t(`${PATH}/classifyHint`)}</Text>
            {RVP_CLASSIFICATION_OPTIONS.map((opt) => (
              <TouchableOpacity
                key={`${opt.condition}-${opt.category}`}
                style={styles.classifyOption}
                onPress={() => {
                  const target = classifyTarget;
                  setClassifyTarget(null);
                  recordVideo(target, opt);
                }}
              >
                <Text style={styles.classifyOptionText}>
                  {t(`${PATH}/${opt.labelKey}`)}
                </Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity
              style={[styles.actionBtn, styles.skipBtn, styles.classifyCancel]}
              onPress={() => setClassifyTarget(null)}
            >
              <Text style={styles.skipBtnText}>{t(`${PATH}/cancel`)}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={!!detailsCrate}
        transparent
        animationType="slide"
        onRequestClose={() => setDetailsCrate(null)}
      >
        <Pressable
          style={styles.modalBackdrop}
          onPress={() => setDetailsCrate(null)}
        >
          <Pressable style={styles.detailsCard} onPress={() => {}}>
            <View style={styles.detailsHeader}>
              <Text style={styles.modalTitle}>
                {t(`${PATH}/itemsIn`, { crate: detailsCrate?.crate_id || "" })}
              </Text>
              <TouchableOpacity
                onPress={() => setDetailsCrate(null)}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Text style={styles.closeIcon}>✕</Text>
              </TouchableOpacity>
            </View>
            {detailsLoading ? (
              <ActivityIndicator
                style={{ marginVertical: 24 }}
                color={colors.primary}
              />
            ) : (
              <FlatList
                data={detailsItems}
                keyExtractor={(it, i) => `${it.name}-${i}`}
                renderItem={({ item: it }) => (
                  <View style={styles.itemRow}>
                    <Image
                      source={{ uri: it.image }}
                      style={styles.itemImage}
                      resizeMode="cover"
                    />
                    <View style={styles.itemInfo}>
                      <Text style={styles.itemName} numberOfLines={2}>
                        {it.name || "—"}
                      </Text>
                      {it.reason ? (
                        <Text style={styles.itemReason}>{it.reason}</Text>
                      ) : null}
                    </View>
                    <Text style={styles.itemQty}>x{it.qty}</Text>
                  </View>
                )}
                ListEmptyComponent={
                  <Text style={styles.emptyText}>{t(`${PATH}/noItems`)}</Text>
                }
                style={styles.itemsList}
              />
            )}
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.primaryBg,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.white,
    paddingHorizontal: 16,
    paddingVertical: 14,
    elevation: 2,
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.mediumBlack,
    marginLeft: 12,
  },
  headerCount: {
    fontSize: 16,
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.plain_green,
  },
  loader: {
    marginTop: 40,
  },
  lockedBanner: {
    backgroundColor: colors.orange,
    padding: 12,
    margin: 12,
    borderRadius: 8,
  },
  lockedText: {
    color: colors.white,
    fontFamily: "NotoSansDevanagari-Bold",
    fontSize: 14,
    textAlign: "center",
  },
  uploadingBanner: {
    backgroundColor: colors.primary,
    padding: 12,
    marginHorizontal: 12,
    marginTop: 12,
    borderRadius: 8,
  },
  listContent: {
    padding: 12,
  },
  sectionHeader: {
    fontSize: 15,
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.mediumBlack,
    marginTop: 8,
    marginBottom: 6,
  },
  card: {
    backgroundColor: colors.white,
    borderRadius: 10,
    padding: 14,
    marginBottom: 10,
    elevation: 1,
  },
  cardTop: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardInfo: {
    flex: 1,
  },
  crateId: {
    fontSize: 16,
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.mediumBlack,
  },
  numItems: {
    fontSize: 13,
    color: colors.lightBlack,
    marginTop: 2,
  },
  qtyRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 12,
  },
  qtyLabel: {
    fontSize: 13,
    color: colors.mediumBlack,
    fontFamily: "NotoSansDevanagari-Bold",
    flex: 1,
  },
  qtyRequired: {
    color: colors.plain_red,
  },
  qtyInput: {
    borderWidth: 1,
    borderColor: colors.lightBlack,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    width: 70,
    marginHorizontal: 8,
    color: colors.mediumBlack,
    textAlign: "center",
  },
  qtyInputMissing: {
    borderColor: colors.plain_red,
  },
  qtySaveBtn: {
    backgroundColor: colors.primary,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 14,
    alignItems: "center",
    justifyContent: "center",
    minWidth: 64,
  },
  qtySaveBtnDisabled: {
    backgroundColor: colors.lightBlack,
  },
  qtySaveBtnText: {
    color: colors.white,
    fontSize: 13,
    fontFamily: "NotoSansDevanagari-Bold",
  },
  chip: {
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  chipText: {
    color: colors.white,
    fontSize: 12,
    fontFamily: "NotoSansDevanagari-Bold",
  },
  detailsCard: {
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 16,
    maxHeight: "75%",
  },
  detailsHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  closeIcon: {
    color: colors.mediumBlack,
    fontSize: 20,
    fontFamily: "NotoSansDevanagari-Bold",
    paddingHorizontal: 4,
  },
  itemsList: {
    marginTop: 4,
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0,0,0,0.06)",
  },
  itemImage: {
    width: 44,
    height: 44,
    borderRadius: 6,
    backgroundColor: colors.primaryBg,
  },
  itemInfo: {
    flex: 1,
    marginLeft: 12,
  },
  itemName: {
    fontSize: 14,
    color: colors.mediumBlack,
  },
  itemReason: {
    fontSize: 12,
    color: colors.lightBlack,
    marginTop: 2,
  },
  itemQty: {
    fontSize: 15,
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.mediumBlack,
    marginLeft: 8,
  },
  videoInfoRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: "rgba(0, 171, 89, 0.08)",
  },
  videoInfoText: {
    fontSize: 13,
    color: colors.mediumBlack,
    fontFamily: "NotoSansDevanagari-Bold",
  },
  discardBtn: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.plain_red,
  },
  discardIcon: {
    color: colors.white,
    fontSize: 13,
    fontFamily: "NotoSansDevanagari-Bold",
  },
  cardActions: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 12,
  },
  actionBtn: {
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
    marginBottom: 8,
    minWidth: 104,
  },
  recordBtn: {
    backgroundColor: colors.primary,
  },
  recordBtnText: {
    color: colors.white,
    fontFamily: "NotoSansDevanagari-Bold",
    fontSize: 14,
  },
  skipBtn: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.lightBlack,
  },
  skipBtnText: {
    color: colors.mediumBlack,
    fontSize: 14,
  },
  emptyText: {
    textAlign: "center",
    marginTop: 40,
    color: colors.lightBlack,
    fontSize: 14,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    padding: 24,
  },
  modalCard: {
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 18,
  },
  modalTitle: {
    fontSize: 16,
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.mediumBlack,
  },
  modalWarning: {
    fontSize: 13,
    color: colors.plain_red,
    marginTop: 6,
  },
  reasonInput: {
    borderWidth: 1,
    borderColor: colors.lightBlack,
    borderRadius: 8,
    minHeight: 70,
    marginTop: 12,
    padding: 10,
    textAlignVertical: "top",
    color: colors.mediumBlack,
  },
  modalActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 14,
  },
  classifyHint: {
    fontSize: 13,
    color: colors.lightBlack,
    marginTop: 6,
    marginBottom: 12,
  },
  classifyOption: {
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 10,
  },
  classifyOptionText: {
    color: colors.mediumBlack,
    fontFamily: "NotoSansDevanagari-Bold",
    fontSize: 15,
    textAlign: "center",
  },
  classifyCancel: {
    alignSelf: "center",
    marginTop: 2,
  },
});

export default CrateHandoverVideos;
